import { Link, createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import BackButton from '@/components/ui/BackButton'
import Badge from '@/components/ui/Badge'
import EmptyState from '@/components/ui/EmptyState'
import { stateActionClass } from '@/components/ui/empty-state-variants'
import Skeleton from '@/components/ui/Skeleton'
import TitleRow from '@/components/ui/TitleRow'
import SafeHtml from '@/features/courses/components/SafeHtml'
import { useSuspensePost } from '@/features/posts/api/queries'
import { formatDate } from '@/lib/format'
import type { PostDetail } from '@/schemas/posts'
import { loadPost } from '@/screens/posts/PostScreen.loader'
import { parsePostId } from '@/screens/posts/lib/parsePostId'

/** Home's "Latest from MoneyMitra" row is the only way in, so back is Home. */
const BACK = '/'

/** The newsletter cover's card: a hairline in the light theme, none in the dark. */
const ARTICLE =
  'overflow-hidden border border-border-subtle bg-card text-card-foreground dark:border-transparent'

const ARTICLE_BODY = 'flex flex-col gap-6 px-5 py-6 sm:px-8'

function PostNotFound({ description }: { description: string }) {
  return (
    <EmptyState
      kind="not-found"
      size="page"
      headingLevel="h1"
      title="Post not found"
      description={description}
      action={
        <Link to={BACK} className={stateActionClass('page')}>
          Back to Home
        </Link>
      }
    />
  )
}

/**
 * Keyed by the id so moving from one post to another remounts the screen. A param that is not a
 * post id is answered here, before the suspense read: a suspense hook cannot be `enabled: false`.
 */
function PostRoute() {
  const postId = parsePostId(Route.useParams().postId)
  if (postId === null) {
    return (
      <ScreenLayout pageTitle="Post not found">
        <PostNotFound description="That address does not name a MoneyMitra post." />
      </ScreenLayout>
    )
  }
  return <PostScreen key={postId} postId={postId} />
}

/** The loaded `TitleRow` with bars for its words: the 36px title line and the 20px meta line. */
function PostHeaderSkeleton() {
  return (
    <div className="flex items-start gap-2">
      <div className="flex h-9 shrink-0 items-center">
        <BackButton to={BACK} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex h-9 items-center">
          <Skeleton className="h-6 w-96 max-w-full rounded-sm" />
        </div>
        <div className="flex h-5 items-center">
          <Skeleton className="h-3 w-56 rounded-xs" />
        </div>
      </div>
    </div>
  )
}

/** The article card before its data: the 16:9 image, a few body lines, the tag row. */
function ArticleSkeleton() {
  return (
    <div className={ARTICLE}>
      <Skeleton className="aspect-video w-full rounded-none" />
      <div className={ARTICLE_BODY}>
        <div className="flex flex-col gap-3">
          <Skeleton className="h-4 w-full rounded-xs" />
          <Skeleton className="h-4 w-11/12 rounded-xs" />
          <Skeleton className="h-4 w-full rounded-xs" />
          <Skeleton className="h-4 w-2/3 rounded-xs" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-6 w-24 rounded-none" />
          <Skeleton className="h-6 w-32 rounded-none" />
        </div>
      </div>
    </div>
  )
}

function PostPending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading this post…"
      header={<PostHeaderSkeleton />}
      pageTitle="Post"
    >
      <ArticleSkeleton />
    </ScreenLayout>
  )
}

/** A genuine failure only: `POST_NOT_FOUND` resolves to `null` and never reaches here. */
function PostError(props: ErrorComponentProps) {
  return (
    <RouteErrorPage
      {...props}
      header={
        <div className="flex h-9 items-center">
          <BackButton to={BACK} />
        </div>
      }
      title="Couldn't load this post"
      description="Check your internet and try again."
    />
  )
}

export const Route = createFileRoute('/_app/posts/$postId')({
  loader: loadPost,
  pendingComponent: PostPending,
  errorComponent: PostError,
  component: PostRoute,
})

/** The summary, at the newsletter cover's summary rung. Blank is no paragraph. */
function Summary({ text }: { text: string }) {
  return text.trim() === '' ? null : (
    <p className="text-h-section font-normal text-muted-foreground">{text}</p>
  )
}

/** A premium post this member's plan doesn't include. Built like `UpgradeRequired`. */
function PostPaywall() {
  return (
    <EmptyState
      kind="not-available"
      size="section"
      title="This post needs a paid plan"
      description="Your current plan doesn't include premium posts. See which plans do."
      action={
        <Link to="/subscription" className={stateActionClass('section')}>
          See plans
        </Link>
      }
    />
  )
}

function ArticleBody({ post }: { post: PostDetail }): ReactNode {
  switch (post.body.kind) {
    case 'locked':
      return (
        <>
          <Summary text={post.summary} />
          <PostPaywall />
        </>
      )
    case 'empty':
      return <Summary text={post.summary} />
    case 'html':
      // The article's `h1` is the title row, so the post's own top heading reads as an h2.
      return <SafeHtml html={post.body.html} headingLevel={2} className="text-body-l" />
  }
}

/**
 * One blog post, read in the app: the title row (category · read time · published date under the
 * title), then the article card: the hero image, the body, and the tags as plain chips (there is
 * no tag page to link them to). A locked post shows its summary and the paywall instead of the
 * body, and no tags.
 */
function PostScreen({ postId }: { postId: number }) {
  const { data: post } = useSuspensePost(postId)

  if (post === null) {
    return (
      <ScreenLayout pageTitle="Post not found">
        <PostNotFound description="We could not find that post. It may have been taken down." />
      </ScreenLayout>
    )
  }

  const meta = [
    post.category,
    post.readTimeMinutes > 0 ? `${String(post.readTimeMinutes)} min read` : null,
    formatDate(post.publishedAt),
    // An unreadable date prints "—"; the line leaves it out rather than end on a dash.
  ].filter((part): part is string => part !== null && part !== '' && part !== '—')

  return (
    <ScreenLayout
      header={<TitleRow title={post.title} back={BACK} subtitle={meta.join(' · ')} />}
      pageTitle={post.title}
    >
      <article className={ARTICLE}>
        {post.imageUrl === null ? null : (
          // Decorative: the title row above names the post.
          <img src={post.imageUrl} alt="" className="aspect-video w-full bg-muted object-cover" />
        )}
        <div className={ARTICLE_BODY}>
          <ArticleBody post={post} />
          {post.body.kind === 'locked' || post.tags.length === 0 ? null : (
            <ul aria-label="Tags" className="flex flex-wrap gap-2">
              {post.tags.map((tag) => (
                <li key={tag}>
                  <Badge size="md">{tag}</Badge>
                </li>
              ))}
            </ul>
          )}
        </div>
      </article>
    </ScreenLayout>
  )
}
